"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnSizingState,
  type ColumnSizingInfoState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { z } from "zod";
import { useDebounce } from "@/hooks/use-debounce";
import { http } from "@/lib/http";
import useDataStore from "@/stores/data-store";
import useSessionStore from "@/stores/session-store";
import { DataTableFilterField } from "@/types/common-type";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const COLUMN_SIZING_CONFIG_KEY_SUFFIX = "columnSizing";
const COLUMN_SIZING_CACHE_KEY_PREFIX = "datatable.columnSizing";
const columnSizingMemoryCache = new Map<string, ColumnSizingState>();
const DEFAULT_SCREEN_BUCKET = "lg";
const initialColumnSizingInfoState: ColumnSizingInfoState = {
  columnSizingStart: [],
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  startOffset: null,
  startSize: null,
};

function normalizeScreenCode(pathname: string) {
  const normalized = pathname.replace(/^\/[^/]+/, "");
  return normalized.length > 0 ? normalized : "/";
}

function isDynamicPathSegment(segment: string) {
  return (
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      segment
    )
  );
}

function buildConfigKey(pathname: string, configKey: string) {
  const normalizedScreenCode = normalizeScreenCode(pathname)
    .split("/")
    .map((segment) => (isDynamicPathSegment(segment) ? "[id]" : segment))
    .join("/")
    .replace(/^\//, "")
    .replace(/\//g, ".");

  return `${normalizedScreenCode || "root"}.${configKey}`;
}

function getScreenBucket(width: number) {
  if (width < 640) return "sm";
  if (width < 768) return "md";
  if (width < 1024) return "lg";
  if (width < 1280) return "xl";
  return "2xl";
}

function getColumnSizingCacheStorageKey(configKey: string) {
  return `${COLUMN_SIZING_CACHE_KEY_PREFIX}.${configKey}`;
}

function getUserScopedColumnSizingCacheStorageKey(
  username: string | undefined,
  configKey: string
) {
  return getColumnSizingCacheStorageKey(
    username ? `${username}.${configKey}` : configKey
  );
}

function readCachedColumnSizing(
  username: string | undefined,
  configKey: string
) {
  const storageKey = getUserScopedColumnSizingCacheStorageKey(
    username,
    configKey
  );
  const inMemoryValue = columnSizingMemoryCache.get(storageKey);

  if (inMemoryValue) {
    return inMemoryValue;
  }

  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as ColumnSizingState;
    columnSizingMemoryCache.set(storageKey, parsedValue);
    return parsedValue;
  } catch (error) {
    console.error("Failed to read datatable column sizing cache", error);
    return {};
  }
}

function writeCachedColumnSizing(
  username: string | undefined,
  configKey: string,
  columnSizing: ColumnSizingState
) {
  const storageKey = getUserScopedColumnSizingCacheStorageKey(
    username,
    configKey
  );
  columnSizingMemoryCache.set(storageKey, columnSizing);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(columnSizing));
  } catch (error) {
    console.error("Failed to write datatable column sizing cache", error);
  }
}

interface UseDataTableProps<TData, TValue> {
  /**
   * The data for the table.
   * @default []
   * @type TData[]
   */
  data: TData[];

  /**
   * The columns of the table.
   * @default []
   * @type ColumnDef<TData, TValue>[]
   */
  columns: ColumnDef<TData, TValue>[];

  /**
   * The number of pages in the table.
   * @type number
   */
  pageCount?: number;

  /**
   * The current page number of the displayed server data.
   * Useful when UI navigation state changes before new data is hydrated.
   */
  currentPage?: number;

  /**
   * The current page size of the displayed server data.
   */
  currentPageSize?: number;

  /**
   * The default number of rows per page.
   * @default 10
   * @type number | undefined
   * @example 20
   */
  defaultPerPage?: number;

  /**
   * The default sort order.
   * @default undefined
   * @type `${Extract<keyof TData, string | number>}.${"asc" | "desc"}` | undefined
   * @example "createdAt.desc"
   */
  defaultSort?: `${Extract<keyof TData, string | number>}.${"asc" | "desc"}`;

  /**
   * Defines filter fields for the table. Supports both dynamic faceted filters and search filters.
   * - Faceted filters are rendered when `options` are provided for a filter field.
   * - Otherwise, search filters are rendered.
   *
   * The indie filter field `value` represents the corresponding column name in the database table.
   * @default []
   * @type { label: string, value: keyof TData, placeholder?: string, options?: { label: string, value: string, icon?: ComponentType<{ className?: string }> }[] }[]
   * @example
   * ```ts
   * // Render a search filter
   * const filterFields = [
   *   { label: "Title", value: "title", placeholder: "Search titles" }
   * ];
   * // Render a faceted filter
   * const filterFields = [
   *   {
   *     label: "Status",
   *     value: "status",
   *     options: [
   *       { label: "Todo", value: "todo" },
   *       { label: "In Progress", value: "in-progress" },
   *       { label: "Done", value: "done" },
   *       { label: "Canceled", value: "canceled" }
   *     ]
   *   }
   * ];
   * ```
   */
  filterFields?: DataTableFilterField<TData>[];

  /**
   * Enable notion like column filters.
   * Advanced filters and column filters cannot be used at the same time.
   * @default false
   * @type boolean
   */
  enableAdvancedFilter?: boolean;

  /**
   * Enable column resizing for the table.
   * @default false
   */
  enableColumnResizing?: boolean;

  /**
   * Add a suffix for table-specific persisted settings.
   * Useful when multiple tables live on the same route.
   */
  columnSizingKeySuffix?: string;
}

const schema = z.object({
  page: z.coerce.number().default(1),
  per_page: z.coerce.number().optional(),
  sort: z.string().trim().optional(),
});

export function useDataTable<TData, TValue>({
  data,
  columns,
  pageCount,
  currentPage,
  currentPageSize,
  defaultPerPage = 20,
  defaultSort,
  filterFields = [],
  enableAdvancedFilter = false,
  enableColumnResizing = true,
  columnSizingKeySuffix,
}: UseDataTableProps<TData, TValue>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { accessToken, username } = useSessionStore();
  const { userConfigs, hasLoadedUserConfigs, setCachedUserConfig } =
    useDataStore();
  const [screenBucket, setScreenBucket] = useState(DEFAULT_SCREEN_BUCKET);
  const [hasResolvedScreenBucket, setHasResolvedScreenBucket] = useState(false);
  const baseColumnSizingConfigKey = useMemo(
    () => {
      const baseKey = buildConfigKey(pathname, COLUMN_SIZING_CONFIG_KEY_SUFFIX);
      if (!columnSizingKeySuffix) {
        return baseKey;
      }

      return baseKey.replace(
        `.${COLUMN_SIZING_CONFIG_KEY_SUFFIX}`,
        `.${columnSizingKeySuffix}.${COLUMN_SIZING_CONFIG_KEY_SUFFIX}`
      );
    },
    [columnSizingKeySuffix, pathname]
  );
  const columnSizingConfigKey = useMemo(
    () => `${baseColumnSizingConfigKey}.${screenBucket}`,
    [baseColumnSizingConfigKey, screenBucket]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setScreenBucket(getScreenBucket(window.innerWidth));
    setHasResolvedScreenBucket(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateScreenBucket = () => {
      const nextBucket = getScreenBucket(window.innerWidth);
      setScreenBucket((prev) => (prev === nextBucket ? prev : nextBucket));
    };

    window.addEventListener("resize", updateScreenBucket);

    return () => {
      window.removeEventListener("resize", updateScreenBucket);
    };
  }, []);

  // Search params
  const search = schema.parse(Object.fromEntries(searchParams));
  const page = search.page;
  const perPage = search.per_page ?? defaultPerPage;
  const sort = search.sort ?? defaultSort;
  const [column, order] = sort?.split(".") ?? [];

  // Memoize computation of searchableColumns and filterableColumns
  const { searchableColumns, filterableColumns } = useMemo(() => {
    return {
      searchableColumns: filterFields.filter((field) => !field.options),
      filterableColumns: filterFields.filter((field) => field.options),
    };
  }, [filterFields]);

  // Create query string
  const createQueryString = useCallback(
    (params: Record<string, string | number | null>, base?: string) => {
      const newSearchParams = new URLSearchParams(
        base ?? searchParams?.toString()
      );

      for (const [key, value] of Object.entries(params)) {
        if (value === null) {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, String(value));
        }
      }

      return newSearchParams.toString();
    },
    [searchParams]
  );

  // Initial column filters
  const initialColumnFilters: ColumnFiltersState = useMemo(() => {
    return Array.from(searchParams.entries()).reduce<ColumnFiltersState>(
      (filters, [key, value]) => {
        const filterableColumn = filterableColumns.find(
          (column) => column.value === key
        );
        const searchableColumn = searchableColumns.find(
          (column) => column.value === key
        );

        if (filterableColumn) {
          filters.push({
            id: key,
            value: value.split("."),
          });
        } else if (searchableColumn) {
          filters.push({
            id: key,
            value: [value],
          });
        }

        return filters;
      },
      []
    );
  }, [filterableColumns, searchableColumns, searchParams]);

  // Table states
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(initialColumnFilters);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] =
    useState<ColumnSizingInfoState>(initialColumnSizingInfoState);
  const [hasLoadedColumnSizingFromCache, setHasLoadedColumnSizingFromCache] =
    useState(!enableColumnResizing);
  const [hasAppliedStoreColumnSizing, setHasAppliedStoreColumnSizing] =
    useState(false);
  const hasHydratedColumnSizingRef = useRef(false);
  const hasLoadedCachedColumnSizingRef = useRef(false);
  const wasResizingColumnRef = useRef(false);

  useEffect(() => {
    setHasAppliedStoreColumnSizing(false);
  }, [columnSizingConfigKey, username]);

  // Handle server-side pagination
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: page - 1,
    pageSize: perPage,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );
  const [displayedPage, setDisplayedPage] = useState(currentPage ?? page);
  const [displayedPageSize, setDisplayedPageSize] = useState(
    currentPageSize ?? perPage
  );
  const latestDisplayedPageRef = useRef(currentPage ?? page);
  const latestDisplayedPageSizeRef = useRef(currentPageSize ?? perPage);

  useEffect(() => {
    latestDisplayedPageRef.current = currentPage ?? page;
    latestDisplayedPageSizeRef.current = currentPageSize ?? perPage;
  }, [currentPage, currentPageSize, page, perPage]);

  useEffect(() => {
    setDisplayedPage(latestDisplayedPageRef.current);
    setDisplayedPageSize(latestDisplayedPageSizeRef.current);
  }, [currentPage, currentPageSize, data]);

  // Latest query string pushed by the sync effects below. The effects of one render (and the StrictMode
  // re-run) all see the same stale searchParams, so they build on this value instead of pushing duplicates.
  const lastQueryStringRef = useRef(searchParams?.toString() ?? "");

  useEffect(() => {
    lastQueryStringRef.current = searchParams?.toString() ?? "";
  }, [searchParams]);

  useEffect(() => {
    const queryString = createQueryString(
      {
        page: pageIndex + 1,
        per_page: pageSize,
      },
      lastQueryStringRef.current
    );

    // Skip no-op navigation: every push re-renders the server page and refetches its data
    if (queryString === lastQueryStringRef.current) return;

    lastQueryStringRef.current = queryString;
    router.push(`${pathname}?${queryString}`, {
      scroll: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  // Handle server-side sorting
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: column ?? "",
      desc: order === "desc",
    },
  ]);

  useEffect(() => {
    const queryString = createQueryString(
      {
        page,
        sort: sorting[0]?.id
          ? `${sorting[0]?.id}.${sorting[0]?.desc ? "desc" : "asc"}`
          : null,
      },
      lastQueryStringRef.current
    );

    // Skip no-op navigation: every push re-renders the server page and refetches its data
    if (queryString === lastQueryStringRef.current) return;

    lastQueryStringRef.current = queryString;
    router.push(`${pathname}?${queryString}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // Handle server-side filtering
  const debouncedSearchableColumnFilters = JSON.parse(
    useDebounce(
      JSON.stringify(
        columnFilters.filter((filter) => {
          return searchableColumns.find((column) => column.value === filter.id);
        })
      ),
      500
    )
  ) as ColumnFiltersState;

  const filterableColumnFilters = columnFilters.filter((filter) => {
    return filterableColumns.find((column) => column.value === filter.id);
  });

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Opt out when advanced filter is enabled, because it contains additional params
    if (enableAdvancedFilter) return;

    // Prevent resetting the page on initial render
    if (!mounted) {
      setMounted(true);
      return;
    }

    // Initialize new params
    const newParamsObject = {
      page: 1,
    };

    // Handle debounced searchable column filters
    for (const column of debouncedSearchableColumnFilters) {
      if (typeof column.value === "string") {
        Object.assign(newParamsObject, {
          [column.id]: typeof column.value === "string" ? column.value : null,
        });
      }
    }

    // Handle filterable column filters
    for (const column of filterableColumnFilters) {
      if (typeof column.value === "object" && Array.isArray(column.value)) {
        Object.assign(newParamsObject, { [column.id]: column.value.join(".") });
      }
    }

    // Remove deleted values
    for (const key of searchParams.keys()) {
      if (
        (searchableColumns.find((column) => column.value === key) &&
          !debouncedSearchableColumnFilters.find(
            (column) => column.id === key
          )) ||
        (filterableColumns.find((column) => column.value === key) &&
          !filterableColumnFilters.find((column) => column.id === key))
      ) {
        Object.assign(newParamsObject, { [key]: null });
      }
    }

    // After cumulating all the changes, push new params
    router.push(`${pathname}?${createQueryString(newParamsObject)}`);

    table.setPageIndex(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(debouncedSearchableColumnFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filterableColumnFilters),
  ]);

  useLayoutEffect(() => {
    if (!enableColumnResizing) {
      setHasLoadedColumnSizingFromCache(true);
      return;
    }

    if (!hasResolvedScreenBucket) {
      setHasLoadedColumnSizingFromCache(false);
      return;
    }

    setHasLoadedColumnSizingFromCache(false);

    const cachedColumnSizing =
      readCachedColumnSizing(username, columnSizingConfigKey) || {};
    const fallbackCachedColumnSizing =
      Object.keys(cachedColumnSizing).length > 0
        ? cachedColumnSizing
        : readCachedColumnSizing(username, baseColumnSizingConfigKey);

    if (
      fallbackCachedColumnSizing &&
      Object.keys(fallbackCachedColumnSizing).length > 0
    ) {
      hasLoadedCachedColumnSizingRef.current = true;
      setColumnSizing(fallbackCachedColumnSizing);
      setHasLoadedColumnSizingFromCache(true);
      return;
    }

    hasLoadedCachedColumnSizingRef.current = false;
    setColumnSizing({});
    setHasLoadedColumnSizingFromCache(true);
  }, [
    baseColumnSizingConfigKey,
    columnSizingConfigKey,
    enableColumnResizing,
    hasResolvedScreenBucket,
    username,
  ]);

  useEffect(() => {
    if (
      !enableColumnResizing ||
      hasAppliedStoreColumnSizing ||
      !hasLoadedUserConfigs
    ) {
      return;
    }

    if (hasLoadedCachedColumnSizingRef.current) {
      setHasAppliedStoreColumnSizing(true);
      return;
    }

    const storedColumnSizing =
      userConfigs[columnSizingConfigKey] ??
      userConfigs[baseColumnSizingConfigKey];

    if (!storedColumnSizing) {
      setHasAppliedStoreColumnSizing(true);
      return;
    }

    try {
      const parsedValue = JSON.parse(storedColumnSizing) as ColumnSizingState;
      writeCachedColumnSizing(username, columnSizingConfigKey, parsedValue);
      hasHydratedColumnSizingRef.current = true;
      setColumnSizing(parsedValue);
    } catch (error) {
      console.error("Failed to parse datatable column sizing config", error);
    } finally {
      setHasAppliedStoreColumnSizing(true);
    }
  }, [
    baseColumnSizingConfigKey,
    columnSizingConfigKey,
    enableColumnResizing,
    hasAppliedStoreColumnSizing,
    hasLoadedUserConfigs,
    userConfigs,
    username,
  ]);

  useEffect(() => {
    if (!hasHydratedColumnSizingRef.current) {
      return;
    }

    hasHydratedColumnSizingRef.current = false;
  }, [columnSizing]);

  useEffect(() => {
    if (
      !enableColumnResizing ||
      !hasLoadedColumnSizingFromCache ||
      !accessToken
    ) {
      wasResizingColumnRef.current = false;
      return;
    }

    const isResizing = Boolean(columnSizingInfo.isResizingColumn);

    if (isResizing) {
      wasResizingColumnRef.current = true;
      return;
    }

    if (!wasResizingColumnRef.current || hasHydratedColumnSizingRef.current) {
      return;
    }

    wasResizingColumnRef.current = false;
    writeCachedColumnSizing(username, columnSizingConfigKey, columnSizing);
    setCachedUserConfig(columnSizingConfigKey, JSON.stringify(columnSizing));

    void http
      .put("/v1/payroll/user-configs/current", {
        key: columnSizingConfigKey,
        value: JSON.stringify(columnSizing),
      })
      .catch((error) => {
        console.error("Failed to save datatable column sizing config", error);
      });
  }, [
    accessToken,
    columnSizing,
    columnSizingInfo.isResizingColumn,
    columnSizingConfigKey,
    enableColumnResizing,
    hasLoadedColumnSizingFromCache,
    setCachedUserConfig,
    username,
  ]);

  const isColumnSizingReady =
    (!enableColumnResizing && hasResolvedScreenBucket) ||
    (!enableColumnResizing && typeof window === "undefined") ||
    (hasResolvedScreenBucket &&
      hasLoadedColumnSizingFromCache &&
      (hasAppliedStoreColumnSizing || !hasLoadedUserConfigs));

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 96,
    },
    pageCount: pageCount ?? Math.ceil(data.length / perPage),
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      columnSizing,
      columnSizingInfo,
    },
    meta: {
      isColumnSizingReady,
      currentPage: displayedPage,
      currentPageSize: displayedPageSize,
    },
    enableRowSelection: true,
    enableColumnResizing,
    columnResizeMode: "onChange",
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  return { table, isColumnSizingReady };
}
